"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { customInstance } from "@/lib/api/client";

import type { ProjectSequencesByJobParams } from "../lib/by-job-query-params";
import { projectSequencesQueryKey } from "./use-project-sequences";
import type { SequenceListItem } from "./use-sequences";

export interface SequenceJobGroup {
  job_id: string;
  job_name: string;
  latest_sequence_updated_at: string;
  sequence_count: number;
  sequences: SequenceListItem[];
}

interface SequencesByJobPage {
  jobs: SequenceJobGroup[];
  page: number;
  per_page: number;
  total_jobs: number;
  total_sequences: number;
}

interface UseProjectSequencesByJobOptions {
  projectId: string | null | undefined;
  enabled?: boolean;
  page?: number;
  perPage?: number;
  params?: ProjectSequencesByJobParams;
}

function buildUrl(
  projectId: string,
  params: ProjectSequencesByJobParams & {
    page: number;
    per_page: number;
  },
): string {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page));
  qs.set("per_page", String(params.per_page));
  if (params.search) qs.set("search", params.search);
  if (params.job_id) {
    qs.set("job_id", params.job_id);
    qs.set("job_op", params.job_op ?? "is");
  }
  if (params.steps != null) {
    qs.set("steps", String(params.steps));
    qs.set("steps_op", params.steps_op ?? "gte");
  }
  if (params.enrollments != null) {
    qs.set("enrollments", String(params.enrollments));
    qs.set("enrollments_op", params.enrollments_op ?? "gte");
  }
  return `/api/projects/${projectId}/sequences/by-job?${qs.toString()}`;
}

/**
 * Paginated query for ``GET /api/projects/{id}/sequences/by-job``.
 * Pages are sequences (newest first), regrouped by job for the sheet UI.
 * Search / filter params are sent to the backend and included in the
 * query key so changes re-request from page one.
 */
export function useProjectSequencesByJob({
  projectId,
  enabled = true,
  page = 1,
  perPage = 10,
  params,
}: UseProjectSequencesByJobOptions) {
  const filterParams = params ?? {};
  const query = useQuery({
    queryKey: [
      ...projectSequencesQueryKey(projectId),
      "by-job",
      { page, perPage, ...filterParams },
    ],
    queryFn: () =>
      customInstance<SequencesByJobPage>(
        buildUrl(projectId!, {
          ...filterParams,
          page,
          per_page: perPage,
        }),
        { method: "GET" },
      ),
    enabled: !!projectId && enabled,
    placeholderData: keepPreviousData,
  });

  return {
    jobs: query.data?.jobs ?? [],
    totalJobs: query.data?.total_jobs ?? 0,
    totalSequences: query.data?.total_sequences ?? 0,
    page: query.data?.page ?? page,
    perPage: query.data?.per_page ?? perPage,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
